import * as React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from "react-hook-form"

import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"

const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
)

// react-hook-form 7.83 declares `ControllerProps<TFieldValues, TName, TTransformedValues>` and
// `Control<TFieldValues, TContext, TTransformedValues>`. TTransformedValues must be threaded
// through here, otherwise every caller passing a `control` from a `useForm` with a resolver gets
// a variance mismatch on the third parameter. Do not "clean up" the unused-looking parameter.
const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues
>({
  ...props
}: ControllerProps<TFieldValues, TName, TTransformedValues>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState, formState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>")
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
  /** Set by a `FormLabel required` inside this item; drives `aria-required`. */
  required?: boolean
  setRequired?: (required: boolean) => void
  /** Set while a `FormDescription` is mounted, so `aria-describedby` never points at a missing node. */
  hasDescription?: boolean
  setHasDescription?: (mounted: boolean) => void
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue
)

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId()
  const [required, setRequired] = React.useState(false)
  const [hasDescription, setHasDescription] = React.useState(false)
  const value = React.useMemo(
    () => ({ id, required, setRequired, hasDescription, setHasDescription }),
    [id, required, hasDescription]
  )

  return (
    <FormItemContext.Provider value={value}>
      <div ref={ref} className={cn("space-y-2", className)} {...props} />
    </FormItemContext.Provider>
  )
})
FormItem.displayName = "FormItem"

/**
 * The required star after a label. Visual only: the control carries
 * `aria-required`. For a label outside `FormField`/`FormItem`, pair it with a
 * plain `Label`.
 */
function RequiredMarker() {
  return (
    <span aria-hidden="true" className="ml-1 text-destructive">
      *
    </span>
  )
}

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & {
    /** Renders the required marker and sets `aria-required` on the item's control. */
    required?: boolean
  }
>(({ className, required = false, children, ...props }, ref) => {
  const { error, formItemId } = useFormField()
  const { setRequired } = React.useContext(FormItemContext)

  React.useLayoutEffect(() => {
    if (!setRequired) return
    setRequired(required)
    return () => setRequired(false)
  }, [required, setRequired])

  return (
    <Label
      ref={ref}
      className={cn(error && "text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    >
      {children}
      {required && <RequiredMarker />}
    </Label>
  )
})
FormLabel.displayName = "FormLabel"

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()
  const { required, hasDescription } = React.useContext(FormItemContext)
  // Only ids of nodes that are rendered: the description while a
  // FormDescription is mounted, the message while there is an error.
  const describedBy =
    [hasDescription ? formDescriptionId : null, error ? formMessageId : null]
      .filter(Boolean)
      .join(" ") || undefined

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={describedBy}
      aria-invalid={!!error}
      aria-required={required || undefined}
      {...props}
    />
  )
})
FormControl.displayName = "FormControl"

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField()
  const { setHasDescription } = React.useContext(FormItemContext)

  React.useLayoutEffect(() => {
    if (!setHasDescription) return
    setHasDescription(true)
    return () => setHasDescription(false)
  }, [setHasDescription])

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
})
FormDescription.displayName = "FormDescription"

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message) : children

  if (!body) {
    return null
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      role="alert"
      className={cn("text-sm font-medium text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  )
})
FormMessage.displayName = "FormMessage"

export {
  Form,
  FormItem,
  FormLabel,
  RequiredMarker,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
}

export { useFormField }